import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PublicIcon from '@mui/icons-material/Public';
import SearchIcon from '@mui/icons-material/Search';
import { Avatar, Box, Chip, InputAdornment, LinearProgress, Pagination, Paper, Stack, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { useToaster } from 'src/utils/toaster/toasterContext';

const getInitial = (contact) => {
  const label = contact.visitor_name || contact.visitor_email || 'Visitor';
  return label.slice(0, 1).toUpperCase();
};

const normalize = (value) => String(value || '').toLowerCase();

const getCountryCode = (contact) => {
  const candidates = [contact?.country_flag, contact?.visitor_country, contact?.country];
  for (const value of candidates) {
    const code = String(value || '')
      .trim()
      .toUpperCase()
      .replace(/[^A-Z]/g, '')
      .slice(0, 2);
    if (/^[A-Z]{2}$/.test(code)) {
      return code;
    }
  }
  return '';
};

const getCountryName = (contact, translate) =>
  contact?.visitor_country || translate('live_chat_contacts_page.unknown_country', { defaultValue: 'Unknown country' });

export default function LiveChatContacts() {
  const { t: translate } = useTranslation();
  const { showToast } = useToaster();
  const [loader, setLoader] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const rowsPerPage = 8;

  const fetchContacts = useCallback(() => {
    setLoader(true);
    HttpServices.Get({
      url: `${userModuleURL.liveChatConversations}?limit=100`,
      successCallback: (data) => {
        const conversations = Array.isArray(data) ? data : data?.conversations || [];
        const contactMap = new Map();
        conversations.forEach((conversation) => {
          const key = conversation.visitor_id || conversation.visitor_email || conversation.id;
          if (!key || contactMap.has(key)) return;
          contactMap.set(key, conversation);
        });
        setContacts(Array.from(contactMap.values()));
        setLoader(false);
      },
      failureCallback: (message) => {
        setLoader(false);
        message && showToast(message, 'e');
      },
    });
  }, [showToast]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const filteredContacts = contacts.filter((contact) => {
    const query = normalize(search);
    if (!query) return true;
    return [contact.visitor_name, contact.visitor_email, contact.visitor_phone, contact.visitor_country]
      .some((value) => normalize(value).includes(query));
  });
  const pageCount = Math.max(1, Math.ceil(filteredContacts.length / rowsPerPage));
  const safePage = Math.min(page, pageCount);
  const paginatedContacts = filteredContacts.slice((safePage - 1) * rowsPerPage, safePage * rowsPerPage);

  return (
    <>
      <Helmet>
        <title>{translate('live_chat_contacts_page.title', { defaultValue: 'Contacts' })}</title>
      </Helmet>

      <Stack spacing={2.5} sx={{ pb: 2 }}>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
          >
            <Box>
              <Typography variant="h5">
                {translate('live_chat_contacts_page.title', { defaultValue: 'Contacts' })}
              </Typography>
            </Box>
            <TextField
              size="small"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={translate('live_chat_contacts_page.search_placeholder', { defaultValue: 'Search contacts' })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', md: 320 } }}
            />
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {loader ? (
            <LinearProgress />
          ) : (
            <Stack>
              {paginatedContacts.map((contact) => {
                const countryCode = getCountryCode(contact);
                return (
                <Stack
                  key={contact.visitor_id || contact.id}
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  sx={{
                    px: 2.5,
                    py: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-of-type': { borderBottom: 0 },
                  }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: countryCode ? '#fff' : 'primary.lighter',
                        color: 'primary.dark',
                        fontWeight: 700,
                        border: countryCode ? '1px solid' : 'none',
                        borderColor: countryCode ? 'divider' : 'transparent',
                      }}
                    >
                      {countryCode ? (
                        <ReactCountryFlag
                          countryCode={countryCode}
                          svg
                          style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover' }}
                          title={countryCode}
                        />
                      ) : (
                        getInitial(contact)
                      )}
                    </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" noWrap>
                      {contact.visitor_name || translate('live_chat_contacts_page.visitor', { defaultValue: 'Visitor' })}
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }} sx={{ mt: 0.5 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                        <EmailIcon fontSize="small" color="disabled" />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {contact.visitor_email || translate('live_chat_contacts_page.no_email', { defaultValue: 'No email' })}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                        <PhoneIcon fontSize="small" color="disabled" />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {contact.visitor_phone || translate('live_chat_contacts_page.no_phone', { defaultValue: 'No phone' })}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                  <Chip
                    variant="outlined"
                    icon={<PublicIcon />}
                    label={getCountryName(contact, translate)}
                    sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
                  />
                </Stack>
                );
              })}
              {!filteredContacts.length && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 5, textAlign: 'center' }}>
                  {translate('live_chat_contacts_page.no_contacts', { defaultValue: 'No contacts found.' })}
                </Typography>
              )}
              {filteredContacts.length > 0 && (
                <Stack direction="row" justifyContent="center" sx={{ py: 2.5 }}>
                  <Pagination
                    count={pageCount}
                    page={safePage}
                    onChange={(_, value) => setPage(value)}
                    color="primary"
                  />
                </Stack>
              )}
            </Stack>
          )}
        </Paper>
      </Stack>
    </>
  );
}
